import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Instagram, X } from "lucide-react";
import { MaskLines, Overline, FadeUp } from "./Reveal";
import { CONTACT } from "./data";
import { scrollToId } from "./SmoothScroll";

const OFFER = [
  ["Acconciatura sposa", "Raccolti, semiraccolti, onde e ricci studiati sul tuo abito."],
  ["Make-up sposa", "Un trucco luminoso e a lunga tenuta, dalla cerimonia all'ultima foto."],
  ["Cerimonia & invitate", "Acconciatura e make-up per mamme, testimoni e ospiti."],
];

export const Bride = () => {
  const ref = useRef(null);
  const [mainIdx, setMainIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  const PHOTOS = [
    { src: "/img/bride/sposa-5.jpg", alt: "Sposa Hair Boutique — look cerimonia con velo e bouquet" },
    { src: "/img/bride/sposa-1.jpg", alt: "Sposa con velo, bouquet azzurro di ortensie" },
    { src: "/img/bride/sposa-2.jpg", alt: "Acconciatura sposa con fiori di gipsofila" },
    { src: "/img/bride/sposa-4.jpg", alt: "Sposa con bouquet bianco e blu, abito ricamato" },
    { src: "/img/bride/sposa-6.jpg", alt: "Sposa sorridente riflessa nello specchio" },
    { src: "/img/bride/sposa-7.jpg", alt: "Sposa con acconciatura riccia e fiori bianchi" },
    { src: "/img/bride/sposa-3.jpg", alt: "Dettaglio make-up sposa — labbra e sguardo" },
  ];
  const main = PHOTOS[mainIdx];

  return (
    <section ref={ref} id="sposa" data-testid="bride-section" className="relative py-24 sm:py-36 overflow-hidden">
      <div className="absolute left-1/2 top-1/3 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-gold/10 blur-[160px] pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 relative">
          <button
            type="button"
            data-testid="bride-main-image"
            onClick={() => setLightbox(true)}
            aria-label="Ingrandisci la foto"
            className="relative aspect-[3/4] rounded-t-full overflow-hidden border border-gold/30 w-full group cursor-zoom-in block"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={main.src}
                src={main.src}
                alt={main.alt}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.03 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
            <p className="absolute bottom-6 left-8 font-mono text-[11px] uppercase tracking-[0.3em] text-ivory pointer-events-none">Hair Boutique · Bridal look</p>
          </button>
          <div className="mt-6 grid grid-cols-6 gap-2 sm:gap-3">
            {PHOTOS.map((p, i) => (
              <button
                key={p.src}
                type="button"
                data-testid={`bride-thumb-${i}`}
                onClick={() => setMainIdx(i)}
                aria-label={`Mostra foto ${i + 1}`}
                aria-current={i === mainIdx}
                className={`aspect-[3/4] overflow-hidden rounded-lg border transition-all duration-500 ${i === mainIdx ? "border-gold ring-1 ring-gold/60" : "border-white/10 hover:border-gold/50"}`}
              >
                <img src={p.src} alt={p.alt} className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" />
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 lg:pl-8">
          <Overline>Bride · Servizio sposa</Overline>
          <h2 className="mt-6 font-display text-5xl sm:text-7xl leading-[0.95] text-ivory">
            <MaskLines lines={["Il giorno", <span key="s">del <em className="text-gold-grad">sì,</em></span>, <span key="t">come l'hai <em className="text-gold-grad">sognato.</em></span>]} />
          </h2>
          <FadeUp delay={0.1}>
            <p className="mt-8 max-w-lg text-sm sm:text-base text-ivory/75 leading-relaxed">
              Acconciatura e make-up per la sposa e per chi la accompagna. Ci prendiamo cura di ogni dettaglio, perché tu possa pensare solo a goderti il tuo giorno.
            </p>
          </FadeUp>
          <ul className="mt-10 border-t border-white/10">
            {OFFER.map(([t, d], i) => (
              <motion.li
                key={t}
                data-testid={`bride-offer-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                className="group flex gap-6 py-6 border-b border-white/10"
              >
                <span className="font-mono text-[11px] text-gold pt-2">0{i + 1}</span>
                <span>
                  <span className="block font-display text-2xl sm:text-3xl text-ivory transition-transform duration-500 group-hover:translate-x-2">{t}</span>
                  <span className="block mt-1 text-sm text-ivory/65">{d}</span>
                </span>
              </motion.li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap gap-4">
            <button data-testid="bride-cta-booking" onClick={() => scrollToId("prenota")} className="btn-gold">
              Richiedi consulenza sposa <ArrowUpRight className="w-4 h-4" />
            </button>
            <a data-testid="bride-instagram-link" href={CONTACT.instagram} target="_blank" rel="noreferrer" className="btn-ghost">
              <Instagram className="w-4 h-4" /> Altri look su Instagram
            </a>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="bride-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setLightbox(false)}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm grid place-items-center p-4 sm:p-10 cursor-zoom-out"
          >
            <motion.img
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              src={main.src}
              alt={main.alt}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl shadow-black"
            />
            <button
              type="button"
              data-testid="bride-lightbox-close"
              onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
              aria-label="Chiudi"
              className="absolute top-5 right-5 z-10 w-11 h-11 rounded-full border border-gold/40 text-gold grid place-items-center hover:bg-gold hover:text-obsidian transition-colors"
            >
              <X className="w-5 h-5 pointer-events-none" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
